import type { OptionItem } from './OptionGroup';
import type { ReactElement } from 'react';

import { GridFilterBtn, GridFilterItem as StyledGridFilterItem } from './OptionGroupStyles';

type GridFilterItemProps = {
  item: OptionItem;
  onSelect: (key: string) => void;
  tabIndex?: number;
};

const getGridSpan = (label: string): string | undefined => {
  const lowerLabel = label.toLowerCase();
  const trimmedLabel = label.trim();

  if (lowerLabel.includes('one size')) {
    return 'full';
  }

  if (
    /^\d+\s+[a-z]{2}\s+[\d.]+$/i.test(trimmedLabel) ||
    /^\d+\s+\([a-z]{2}\s+[\d.]+\)$/i.test(trimmedLabel) ||
    lowerLabel.includes('(years)') ||
    (lowerLabel.includes('jp') && lowerLabel.includes('cm')) ||
    /^\d+\s+\(\d+\)$/.test(trimmedLabel) ||
    /^\d+\s+\(\d+\s+cm\)$/i.test(trimmedLabel) ||
    label.includes('-')
  ) {
    return 'wide';
  }

  return undefined;
};

const removeParentheses = (text: string): string => text.replace(/[()]/g, '');

interface MultiLineResult {
  hasMultiLine: boolean;
  firstLine: string;
  secondLine: string;
}

const parseMultiLineLabel = (label: string): MultiLineResult => {
  const patterns = [
    {
      regex: /^(\d+)\s+([A-Z]{2}\s+[\d.]+)$/i,
      formatter: (match: RegExpExecArray) => ({
        firstLine: match[1],
        secondLine: match[2].toUpperCase(),
      }),
    },
    {
      regex: /^(\d+)\s+(\([A-Z]{2}\s+[\d.]+\))$/i,
      formatter: (match: RegExpExecArray) => ({
        firstLine: match[1],
        secondLine: removeParentheses(match[2]).toUpperCase(),
      }),
    },
    {
      regex: /^([A-Z]{2,3})\s+(\d+)$/i,
      formatter: (match: RegExpExecArray) => ({
        firstLine: match[1].toUpperCase(),
        secondLine: match[2],
      }),
    },
    {
      regex: /^(\d+)\s+\(Years\)$/i,
      formatter: (match: RegExpExecArray) => ({ firstLine: match[1], secondLine: 'Years' }),
    },
    {
      regex: /^(\d+)\s+(JP\s+[\d.]+cm)$/i,
      formatter: (match: RegExpExecArray) => ({ firstLine: match[1], secondLine: match[2] }),
    },
    {
      regex: /^(\d+)\s+\((\d+)\)$/,
      formatter: (match: RegExpExecArray) => ({ firstLine: match[1], secondLine: match[2] }),
    },
    {
      regex: /^(\d+)\s+\((JP\s+[\d.]+cm)\)$/i,
      formatter: (match: RegExpExecArray) => ({ firstLine: match[1], secondLine: match[2] }),
    },
    {
      regex: /^(\d+)\s+\((\d+\s+cm)\)$/i,
      formatter: (match: RegExpExecArray) => ({ firstLine: match[1], secondLine: match[2] }),
    },
  ];

  const matchedPattern = patterns.find(pattern => pattern.regex.test(label));

  if (matchedPattern) {
    const match = matchedPattern.regex.exec(label);

    if (match) {
      const { firstLine, secondLine } = matchedPattern.formatter(match);

      return { hasMultiLine: true, firstLine, secondLine };
    }
  }

  return { hasMultiLine: false, firstLine: '', secondLine: '' };
};

export const GridFilterItem = ({ item, onSelect, tabIndex }: GridFilterItemProps): ReactElement => {
  const span = getGridSpan(item.label);
  const isWide = span === 'wide';

  const { hasMultiLine, firstLine, secondLine } = isWide
    ? parseMultiLineLabel(item.label)
    : { hasMultiLine: false, firstLine: '', secondLine: '' };

  return (
    <StyledGridFilterItem key={item.key} data-span={span}>
      <GridFilterBtn
        type="button"
        role="checkbox"
        className={item.selected ? 'selected' : ''}
        aria-checked={item.selected}
        onClick={() => onSelect(item.key)}
        data-long-label={item.label.length > 6}
        data-wide-label={hasMultiLine}
        tabIndex={tabIndex}
        data-testid={item.testId}
      >
        {hasMultiLine ? (
          <>
            <span>{firstLine}</span>
            <span>{secondLine}</span>
          </>
        ) : (
          removeParentheses(item.label)
        )}
      </GridFilterBtn>
    </StyledGridFilterItem>
  );
};
